import { SwitchNote } from "odyssey-shared";
import type { HudSnapshot } from "../hud/store.ts";

/**
 * Switch and accept (ADR-076's amendment of 2026-10-08, E3): the page asks for the switch to the party's flag through
 * the very request the flag panel's button makes, so every rule of flag switching holds as it does there (the level
 * lock, combat, Hostile zones, the safe zone, the full countdown), and sends the `partyAnswer` only once `pvpState`
 * reports the new preference, that is once the server has completed the switch. The page never shortens or skips a
 * rule, and the gateway checks the preference again at the accept, so a modified page gains nothing.
 */

/**
 * What the sequence needs of the HUD: a way to hear changes, the last preference and switch, and the request that
 * asks the server for a preference, answering whether it went.
 */
export interface SwitchHud {
  readonly subscribe: (listener: () => void) => () => void;
  readonly getSnapshot: () => Pick<HudSnapshot, "preference" | "pvpSwitch">;
  readonly request: (wanted: number) => boolean;
}

/**
 * How a wait ended without the page being told by its caller: `seated` the accept went out; `refused` a `pvpSwitch`
 * frame refused or interrupted the switch, with its note; `unsent` the request never went out.
 */
export type SwitchEnd = { readonly kind: "seated" } | { readonly kind: "refused"; readonly note: number } | { readonly kind: "unsent" };

/**
 * Asks for the switch to the party's flag, then answers the invitation with an accept when the preference reports it.
 * A note counts as the switch's only when it arrives in a `pvpSwitch` frame after the request (a frame is a new
 * `pvpSwitch` object in the HUD's snapshot), so a note left from an earlier switch ends nothing. The wait ends at once
 * when the request did not go and when such a frame refuses or interrupts the switch; the invitation is then left
 * pending so the player can read why and decide within its time.
 *
 * @param hud The HUD.
 * @param answer Answers an invitation, as `PartySource.answer` does.
 * @param invite The invitation's id.
 * @param wanted The party's flag, `PvpFlag.Protected` or `PvpFlag.Flagged`.
 * @param onEnd Told once how the wait ended, unless the returned function ended it.
 * @returns A function that ends the wait, for a prompt that closes first.
 */
export function switchAndAccept(hud: SwitchHud, answer: (invite: number, accept: boolean) => boolean | void, invite: number, wanted: number, onEnd: (end: SwitchEnd) => void = () => undefined): () => void {
  const baseline = hud.getSnapshot().pvpSwitch;
  let stop: () => void = () => undefined;
  let done = false;
  const finish = (): void => {
    done = true;
    stop();
  };
  const end = (how: SwitchEnd): void => {
    finish();
    onEnd(how);
  };
  const check = (): void => {
    if (done) {
      return;
    }
    const { preference, pvpSwitch } = hud.getSnapshot();
    if (preference === wanted) {
      finish();
      answer(invite, true);
      onEnd({ kind: "seated" });
    } else if (pvpSwitch !== baseline && pvpSwitch.endsAt === null && pvpSwitch.note !== SwitchNote.None) {
      end({ kind: "refused", note: pvpSwitch.note });
    }
  };
  stop = hud.subscribe(check);
  if (hud.getSnapshot().preference === wanted) {
    check();
    return finish;
  }
  if (!hud.request(wanted)) {
    end({ kind: "unsent" });
  }
  return finish;
}
